import {DocumentPart, DvDocument, ItemAction, PartType, TemplateType, removePart as removePartAction, useAppDispatch} from '@dv5/domain'
import {clipboardService, ErrorDisplayProcessor, markdownService} from '@dv5/components'
import {documentPartsApi, templatesApi, viewsApi} from '@dv5/api'
import {useCallback, useEffect, useRef, useState} from 'react'
import {documentApi} from '../services/documentService'

interface UseDocumentPartProps {
    documentId: string
    part: DocumentPart
    layers: string[]
    tags: string[]
    onClone: (documentId: string, partId: string) => void
    onAddComment?: () => void
}

interface UseDocumentPartReturn {
    editMode: boolean
    content: string
    setContent: React.Dispatch<React.SetStateAction<string>>
    saveStatus: 'not_changed' | 'changed' | 'saved'
    lastSavedTime: Date | null
    toggleEditMode: () => void
    cancelEdit: () => void
    handleManualSave: () => void
    changeHeader: (newHeader: string | null) => void
    changeType: (newType: PartType) => void
    convertType: (newType: PartType) => void
    showChangeTypeDialog: boolean
    setShowChangeTypeDialog: React.Dispatch<React.SetStateAction<boolean>>
    changeOnView: (partId: string, newContent: string) => void
    copyContent: (action?: string) => void
    handleMenuAction: (action: ItemAction) => void
    removePart: () => void
    handleSaveAsTemplate: (title: string) => void
    handleChangeDocument: (newDocumentId: string) => void
    documents: Array<DvDocument>
    showDeleteConfirmation: boolean
    setShowDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>
    showSaveTemplateDialog: boolean
    setShowSaveTemplateDialog: React.Dispatch<React.SetStateAction<boolean>>
    showChangeDocumentDialog: boolean
    setShowChangeDocumentDialog: React.Dispatch<React.SetStateAction<boolean>>
}

export const useDocumentPart = ({
                                    documentId,
                                    part,
                                    layers,
                                    tags,
                                    onClone,
                                    onAddComment,
                                }: UseDocumentPartProps): UseDocumentPartReturn => {
    const [editMode, setEditMode] = useState(false)
    const [content, setContent] = useState('')
    const [originalContent, setOriginalContent] = useState('')
    const [saveStatus, setSaveStatus] = useState<'not_changed' | 'changed' | 'saved'>('not_changed')
    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false)
    const [showChangeDocumentDialog, setShowChangeDocumentDialog] = useState(false)
    const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false)
    const [documents, setDocuments] = useState<Array<DvDocument>>([])
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const dispatch = useAppDispatch()
    const errorProcessor = new ErrorDisplayProcessor(dispatch)

    const toggleEditMode = useCallback(() => {
        setEditMode(!editMode)
        if (!editMode) {
            setContent(part.partContent)
            setOriginalContent(part.partContent)
            setSaveStatus('not_changed')
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
            if (content !== part.partContent) {
                savePart(true)
            }
        }
    }, [editMode, part.partContent, content])

    const cancelEdit = useCallback(() => {
        setEditMode(false)
        setContent(originalContent)
        setSaveStatus('not_changed')
    }, [originalContent])

    const savePart = useCallback((manualSave: boolean = false) => {
        documentApi.changePartContent(documentId, part.id, content)
        setSaveStatus('saved')
        setLastSavedTime(new Date())
        if (manualSave) {
            setEditMode(false)
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [documentId, part.id, content])

    const handleManualSave = useCallback(() => {
        savePart(true)
    }, [savePart])

    useEffect(() => {
        if (editMode && content !== originalContent) {
            setSaveStatus('changed')
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            timeoutRef.current = setTimeout(() => {
                savePart()
            }, 2000)
        } else if (editMode && content === originalContent) {
            setSaveStatus('not_changed')
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [content, editMode, originalContent, savePart])

    const changeHeader = useCallback((newHeader: string | null) => {
        const normalizedNew = newHeader || ''
        const normalizedCurrent = part.partHeader || ''
        if (normalizedNew === normalizedCurrent) {
            return
        }
        documentApi.changePartHeaderType(documentId, part.id, normalizedNew, part.partType)
    }, [documentId, part.id, part.partHeader, part.partType])

    const changeType = useCallback((newType: PartType) => {
        if (newType === part.partType) {
            return
        }
        documentApi.changePartHeaderType(documentId, part.id, part.partHeader || '', newType.toLowerCase())
    }, [documentId, part.id, part.partHeader, part.partType])

    const changeOnView = useCallback((partId: string, newContent: string) => {
        documentApi.changePartContent(documentId, partId, newContent)
        setSaveStatus('saved')
        setLastSavedTime(new Date())
        setContent(newContent)
    }, [documentId])

    const convertType = useCallback((newType: PartType) => {
        if (newType === part.partType || !Object.values(PartType).includes(newType)) return
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (editMode && content !== part.partContent) {
            savePart()
        }
        setOriginalContent(content)
        changeType(newType)
        setShowChangeTypeDialog(false)
    }, [part.partType, part.partContent, editMode, content, savePart, changeType])

    const copyContent = useCallback((action?: string) => {
        if (action === 'export-csv') {
            documentApi.exportToCsv(part)
            return
        }

        if (action === 'copy-without-header') {
            const textToCopy = part.partType === PartType.MARKDOWN
                ? markdownService.getAsPlainText(markdownService.parseMarkdown(part.partContent))
                : part.partContent
            clipboardService.copyTo(textToCopy, true)
            return
        }

        let textToCopy = ''
        if (action === 'copy' && part.partHeader && part.partHeader.length > 0) {
            textToCopy = part.partHeader + '\n\n'
        }
        if (action === 'copy' && part.partType == PartType.MARKDOWN) {
            textToCopy += markdownService.getAsPlainText(markdownService.parseMarkdown(part.partContent))
        } else {
            textToCopy += part.partContent
        }
        clipboardService.copyTo(textToCopy, true)
    }, [part])

    const loadDocuments = useCallback(() => {
        viewsApi.loadDocuments(errorProcessor, (loadedDocuments) => {
            setDocuments(loadedDocuments)
        }).catch(console.error)
    }, [errorProcessor])

    const handleMenuAction = useCallback((action: ItemAction) => {
        switch (action) {
            case ItemAction.CHANGE_PART_TYPE:
                setShowChangeTypeDialog(true)
                break
            case ItemAction.EDIT: {
                toggleEditMode()
                break
            }
            case ItemAction.MOVE_TO_LAYERS:
                documentApi.changePartLayers(documentId, part.id, layers.join(','))
                break
            case ItemAction.ASSIGN_TO_TAGS:
                documentApi.assignPartToTags(documentId, part.id, tags.join(','))
                break
            case ItemAction.CLONE:
                onClone(documentId, part.id)
                break
            case ItemAction.DELETE:
                setShowDeleteConfirmation(true)
                break
            case ItemAction.SAVE_AS_TEMPLATE:
                setShowSaveTemplateDialog(true)
                break
            case ItemAction.MOVE_TO_DOCUMENT:
                loadDocuments()
                setShowChangeDocumentDialog(true)
                break
            case ItemAction.ADD_COMMENT:
                onAddComment?.()
                break
        }
    }, [documentId, part.id, layers, tags, onClone, toggleEditMode, loadDocuments])

    const removePart = useCallback(() => {
        documentApi.removePart(documentId, part.id)
        setShowDeleteConfirmation(false)
    }, [documentId, part.id])

    const handleSaveAsTemplate = useCallback((title: string) => {
        templatesApi.createTemplate(
            title,
            TemplateType.DOCUMENT_PART,
            {partHeader: part.partHeader || '', partContent: part.partContent, partType: part.partType},
            errorProcessor,
            () => {
                setShowSaveTemplateDialog(false)
            }
        ).catch(console.error)
    }, [part])

    const handleChangeDocument = useCallback((newDocumentId: string) => {
        documentPartsApi.changePartDocument(
            part.id,
            documentId,
            newDocumentId,
            errorProcessor,
            () => {
                dispatch(removePartAction({documentId, changes: part}))
                setShowChangeDocumentDialog(false)
            }
        ).catch(console.error)
    }, [documentId, part, errorProcessor, dispatch])

    return {
        editMode,
        content,
        setContent,
        saveStatus,
        lastSavedTime,
        toggleEditMode,
        cancelEdit,
        handleManualSave,
        changeHeader,
        changeType,
        convertType,
        showChangeTypeDialog,
        setShowChangeTypeDialog,
        changeOnView,
        copyContent,
        handleMenuAction,
        removePart,
        handleSaveAsTemplate,
        handleChangeDocument,
        documents,
        showDeleteConfirmation,
        setShowDeleteConfirmation,
        showSaveTemplateDialog,
        setShowSaveTemplateDialog,
        showChangeDocumentDialog,
        setShowChangeDocumentDialog,
    }
}
